import React, { useEffect, useMemo, useState } from 'react'
import apiClient, { API_ENDPOINTS } from '../../config/api'

const DASHBOARD_API = API_ENDPOINTS.WAREHOUSE_DASHBOARD

const formatNumber = value => Number(value ?? 0).toLocaleString()
const formatCurrency = value => `$${Number(value ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
const formatDate = value => value ? new Date(value).toLocaleString(undefined, {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit'
}) : '—'

const TOP_BAR_GRADIENT = 'linear-gradient(135deg, rgba(19, 56, 181, 0.45), rgba(92, 142, 240, 0.22))'
const BLUE_GLASS_GRADIENT = 'linear-gradient(135deg, rgba(11, 94, 215, 0.5), rgba(98, 179, 255, 0.25))'

const useCountUp = (value, duration = 1200) => {
  const [displayValue, setDisplayValue] = useState(0)

  useEffect(() => {
    const target = Number(value ?? 0)
    let frameId
    let startTimestamp = null

    const animate = timestamp => {
      if (startTimestamp === null) {
        startTimestamp = timestamp
      }
      const progress = Math.min((timestamp - startTimestamp) / duration, 1)
      const currentValue = Math.round(target * progress)
      setDisplayValue(currentValue)
      if (progress < 1) {
        frameId = requestAnimationFrame(animate)
      }
    }

    frameId = requestAnimationFrame(animate)
    return () => cancelAnimationFrame(frameId)
  }, [value, duration])

  return displayValue
}

const MetricCard = ({ title, subtitle, icon, value, footer, gradient = TOP_BAR_GRADIENT, delay = 0, formatValue }) => {
  const animatedValue = useCountUp(value)
  const cardGradient = gradient
  return (
    <div className="col-xxl-3 col-lg-3 col-md-6 col-sm-6">
      <div
        className="card border-0 text-white position-relative overflow-hidden metric-card metric-card-animate"
        style={{
          minHeight: 120,
          borderRadius: 12,
          background: 'rgba(15, 23, 42, 0.55)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 8px 16px rgba(15, 23, 42, 0.25)',
          '--metric-card-delay': `${delay}s`
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: cardGradient,
            opacity: 0.75
          }}
        />
        <div className="card-body position-relative p-3 d-flex flex-column gap-2">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <p className="text-uppercase mb-0 text-white-50" style={{ fontSize: '0.65rem', letterSpacing: '0.08em' }}>{subtitle}</p>
              <span className="fw-semibold" style={{ fontSize: '0.9rem' }}>{title}</span>
            </div>
            <div
              className="rounded-circle d-flex align-items-center justify-content-center"
              style={{
                width: 32,
                height: 32,
                background: 'rgba(255, 255, 255, 0.12)',
                border: '1px solid rgba(255,255,255,0.2)'
              }}
            >
              <i className={`${icon} fs-6`}></i>
            </div>
          </div>
          <div>
            <h5 className="fw-bold mb-1 count-up-number" style={{ fontSize: '1.25rem' }}>
              {formatValue ? formatValue(animatedValue) : formatNumber(animatedValue)}
            </h5>
            {footer && <p className="mb-0" style={{ fontSize: '0.75rem', opacity: 0.7 }}>{footer}</p>}
          </div>
        </div>
      </div>
    </div>
  )
}

const BarChartCard = ({ title, subtitle, data, colors }) => {
  const maxValue = useMemo(() => {
    if (!data.length) {
      return 1
    }
    const max = Math.max(...data.map(item => item.value))
    return Number.isFinite(max) && max > 0 ? max : 1
  }, [data])

  return (
    <div className="card h-100 shadow-sm border-0">
      <div className="card-body">
        <h5 className="card-title">{title} {subtitle && <span className="text-muted fw-normal">| {subtitle}</span>}</h5>
        <div className="d-flex flex-column gap-3">
          {data.map((item, idx) => {
            const percentage = Math.round((item.value / maxValue) * 100)
            return (
              <div key={item.label || item.id}>
                <div className="d-flex justify-content-between mb-1">
                  <span className="fw-semibold">{item.label}</span>
                  <span className="text-muted">{formatNumber(item.value)}</span>
                </div>
                <div className="animated-progress-bar" style={{ height: 10, background: '#edf1f7', borderRadius: 999 }}>
                  <div
                    className="animated-progress-indicator"
                    style={{
                      '--target-width': `${percentage}%`,
                      width: `${percentage}%`,
                      height: '100%',
                      borderRadius: 999,
                      background: colors?.[idx] ?? 'linear-gradient(90deg, #0d6efd, #6610f2)'
                    }}
                  />
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

const EmptyState = ({ icon = 'bi bi-eye-slash', message }) => (
  <div className="text-center py-4 text-muted">
    <i className={`${icon} fs-3 mb-2 d-block`}></i>
    <span>{message}</span>
  </div>
)

const Dashboard = () => {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [deadStockState, setDeadStockState] = useState(null)
  const [deadStockLoading, setDeadStockLoading] = useState(false)
  const [slowStockState, setSlowStockState] = useState(null)
  const [slowStockLoading, setSlowStockLoading] = useState(false)

  useEffect(() => {
    const controller = new AbortController()

    const fetchDashboard = async () => {
      setLoading(true)
      try {
        const response = await apiClient.get(DASHBOARD_API, { signal: controller.signal })
        setData(response.data)
        setDeadStockState(response.data?.dead_stock ?? null)
        setSlowStockState(response.data?.slow_moving_stock ?? null)
        setError(null)
      } catch (err) {
        if (!controller.signal.aborted) {
          setError('We could not load the dashboard right now. Please refresh to try again.')
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false)
        }
      }
    }

    fetchDashboard()
    return () => controller.abort()
  }, [])

  const warehouse = data?.warehouse ?? {}
  const stockEvaluation = data?.stock_evaluation ?? {}
  const lowStockItems = data?.low_stock_items ?? []
  const topProductsByValue = data?.top_products_by_value ?? []
  const recentStockAdditions = data?.recent_stock_additions ?? []
  const stockSummary = data?.stock_summary ?? []
  const deadStock = deadStockState ?? data?.dead_stock ?? {}
  const slowStock = slowStockState ?? data?.slow_moving_stock ?? {}

  const handleDeadStockPeriodClick = async (period) => {
    if (!period || period === deadStock?.selected_period || deadStockLoading) return

    setDeadStockState(prev => ({
      ...(prev || deadStock || {}),
      selected_period: period
    }))

    try {
      setDeadStockLoading(true)
      const response = await apiClient.get(DASHBOARD_API, {
        params: { dead_stock_period: period, period }
      })
      setData(prev => ({ ...(prev || {}), ...response.data }))
      setDeadStockState(response.data?.dead_stock ?? null)
    } catch (e) {
      setDeadStockState(prev => prev || deadStock)
    } finally {
      setDeadStockLoading(false)
    }
  }

  const handleSlowStockPeriodClick = async (period) => {
    if (!period || period === slowStock?.selected_period || slowStockLoading) return

    setSlowStockState(prev => ({
      ...(prev || slowStock || {}),
      selected_period: period
    }))

    try {
      setSlowStockLoading(true)
      const response = await apiClient.get(DASHBOARD_API, {
        params: { slow_stock_period: period, period }
      })
      setData(prev => ({ ...(prev || {}), ...response.data }))
      setSlowStockState(response.data?.slow_moving_stock ?? null)
    } catch (e) {
      setSlowStockState(prev => prev || slowStock)
    } finally {
      setSlowStockLoading(false)
    }
  }

  const categoryChartData = useMemo(() => {
    const stockByCategory = data?.stock_by_category ?? []
    return stockByCategory.map(cat => ({
      id: cat.category_id,
      label: cat.category_name,
      value: cat.total_quantity ?? 0
    }))
  }, [data?.stock_by_category])

  const renderContent = () => {
    if (loading) {
      return (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading dashboard...</span>
          </div>
        </div>
      )
    }

    if (error) {
      return (
        <div className="alert alert-danger d-flex align-items-center" role="alert">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error}
        </div>
      )
    }

    const metricCards = [
      {
        key: 'stock_entries',
        title: 'Stock Entries',
        subtitle: 'Total',
        icon: 'bi bi-boxes',
        value: stockEvaluation.total_stock_entries,
        footer: 'Active inventory records',
        gradient: TOP_BAR_GRADIENT,
        delay: 0
      },
      {
        key: 'total_quantity',
        title: 'Total Quantity',
        subtitle: 'Units in stock',
        icon: 'bi bi-box-seam',
        value: stockEvaluation.total_quantity,
        footer: 'All products combined',
        gradient: TOP_BAR_GRADIENT,
        delay: 0.05
      },
      {
        key: 'total_cost',
        title: 'Total Cost Value',
        subtitle: 'Inventory value',
        icon: 'bi bi-currency-dollar',
        value: stockEvaluation.total_cost_value,
        footer: 'At purchase price',
        gradient: BLUE_GLASS_GRADIENT,
        delay: 0.1,
        formatValue: formatCurrency
      },
      {
        key: 'unique_products',
        title: 'Unique Products',
        subtitle: 'Product variety',
        icon: 'bi bi-collection',
        value: stockEvaluation.unique_products,
        footer: 'Different SKUs',
        gradient: BLUE_GLASS_GRADIENT,
        delay: 0.15
      }
    ]

    return (
      <>
        {/* Warehouse Info Banner */}
        {warehouse.name && (
          <div className="card shadow-sm border-0 mb-4" style={{ background: 'linear-gradient(135deg, rgba(13, 110, 253, 0.1), rgba(13, 110, 253, 0.05))' }}>
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h5 className="mb-1 d-flex align-items-center gap-2">
                    <i className="bi bi-house-gear text-primary"></i>
                    {warehouse.name}
                    {warehouse.is_main && (
                      <span className="badge bg-primary">Main Warehouse</span>
                    )}
                  </h5>
                  {warehouse.location && (
                    <p className="text-muted mb-0 small">
                      <i className="bi bi-geo-alt"></i> {warehouse.location}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="row g-4">
          {metricCards.map(card => (
            <MetricCard key={card.key} {...card} />
          ))}
        </div>

        <div className="row g-4 mt-1">
          <div className="col-lg-6">
            <BarChartCard
              title="Stock by Category"
              subtitle="Quantity distribution"
              data={categoryChartData}
              colors={['linear-gradient(90deg,#0d6efd,#43b0ff)', 'linear-gradient(90deg,#20c997,#0d6efd)', 'linear-gradient(90deg,#6610f2,#d63384)']}
            />
          </div>
          <div className="col-lg-6">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="card-title mb-0 d-flex align-items-center gap-2">
                    <i className="bi bi-trophy text-warning" />
                    Top Products by Value
                  </h5>
                  <span className="badge rounded-pill bg-warning bg-opacity-10 text-warning">
                    Top {topProductsByValue.length}
                  </span>
                </div>
                {topProductsByValue.length ? (
                  <div className="table-responsive">
                    <table className="table align-middle mb-0 table-hover">
                      <thead className="table-light">
                        <tr>
                          <th>Product</th>
                          <th>Quantity</th>
                          <th>Total Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {topProductsByValue.map((item, idx) => (
                          <tr key={`${item.product_id}-${idx}`}>
                            <td>
                              <div className="d-flex align-items-center gap-2">
                                <div className="rounded-circle d-flex align-items-center justify-content-center bg-primary bg-opacity-10 text-primary" style={{ width: 32, height: 32 }}>
                                  <span className="fw-bold small">{idx + 1}</span>
                                </div>
                                <div>
                                  <div className="fw-semibold">{item.product_name}</div>
                                  <small className="text-muted">{item.product_sku}</small>
                                </div>
                              </div>
                            </td>
                            <td>
                              <span className="badge bg-primary bg-opacity-10 text-primary">
                                {formatNumber(item.total_quantity)}
                              </span>
                            </td>
                            <td className="fw-semibold">{formatCurrency(item.total_cost)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <EmptyState message="No products data available." />
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="row g-4 mt-1">
          <div className="col-lg-6">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h5 className="card-title mb-0 d-flex align-items-center gap-2">
                    <i className="bi bi-exclamation-triangle text-danger" />
                    Low Stock Items
                  </h5>
                  <span className="badge rounded-pill bg-danger bg-opacity-10 text-danger">
                    {lowStockItems.length} items
                  </span>
                </div>
                <p className="text-muted small mb-2">
                  Products below reorder level requiring attention.
                </p>
                {lowStockItems.length ? (
                  <div className="table-responsive mt-3">
                    <table className="table align-middle mb-0 table-hover">
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: '35%' }}>Product</th>
                          <th>Current</th>
                          <th>Reorder Level</th>
                          <th>Batch</th>
                        </tr>
                      </thead>
                      <tbody>
                        {lowStockItems.map((item, idx) => (
                          <tr key={`${item.product_id}-${item.batch_number}-${idx}`}>
                            <td>
                              <div className="fw-semibold">{item.product_name}</div>
                              <small className="text-muted">{item.product_sku}</small>
                            </td>
                            <td>
                              <span className={`badge ${item.current_quantity === 0 ? 'bg-danger' : 'bg-warning'} bg-opacity-10 ${item.current_quantity === 0 ? 'text-danger' : 'text-warning'}`}>
                                {formatNumber(item.current_quantity)}
                              </span>
                            </td>
                            <td>{formatNumber(item.reorder_level)}</td>
                            <td>
                              <small className="text-muted">{item.batch_number}</small>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <EmptyState icon="bi bi-check-circle" message="All products are above reorder level." />
                )}
              </div>
            </div>
          </div>

          <div className="col-lg-6">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h5 className="card-title mb-0 d-flex align-items-center gap-2">
                    <i className="bi bi-clock-history text-info" />
                    Recent Stock Additions
                  </h5>
                  <span className="badge rounded-pill bg-info bg-opacity-10 text-info">
                    {recentStockAdditions.length} recent
                  </span>
                </div>
                <p className="text-muted small mb-2">
                  Latest stock entries received in the warehouse.
                </p>
                {recentStockAdditions.length ? (
                  <div className="table-responsive mt-3">
                    <table className="table align-middle mb-0 table-hover">
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: '40%' }}>Product</th>
                          <th>Quantity</th>
                          <th>Received</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recentStockAdditions.slice(0, 7).map((item, idx) => (
                          <tr key={`${item.batch_number}-${idx}`}>
                            <td>
                              <div className="d-flex align-items-center gap-2">
                                <div className="rounded-circle d-flex align-items-center justify-content-center bg-info bg-opacity-10 text-info" style={{ width: 32, height: 32 }}>
                                  <i className="bi bi-box-seam" />
                                </div>
                                <div>
                                  <div className="fw-semibold">{item.product_name}</div>
                                  <small className="text-muted">{item.product_sku}</small>
                                </div>
                              </div>
                            </td>
                            <td>
                              <span className="badge bg-success bg-opacity-10 text-success">
                                {formatNumber(item.quantity)}
                              </span>
                            </td>
                            <td>
                              <small className="text-muted d-block">{formatDate(item.received_date || item.created_at)}</small>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <EmptyState message="No recent stock additions." />
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="row g-4 mt-1">
          <div className="col-12">
            <div className="card shadow-sm border-0">
              <div className="card-body">
                <h5 className="card-title mb-3 d-flex align-items-center gap-2">
                  <i className="bi bi-list-ul text-primary"></i>
                  Stock Summary
                </h5>
                {stockSummary.length ? (
                  <div className="table-responsive">
                    <table className="table align-middle mb-0 table-hover">
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: '30%' }}>Product</th>
                          <th>Category</th>
                          <th>Total Quantity</th>
                          <th>Total Cost</th>
                          <th>Batches</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stockSummary.map((item) => (
                          <tr key={item.product_id}>
                            <td>
                              <div className="fw-semibold">{item.product_name}</div>
                              <small className="text-muted">{item.product_sku}</small>
                            </td>
                            <td>
                              <span className="badge bg-secondary bg-opacity-10 text-secondary">
                                {item.category_name}
                              </span>
                            </td>
                            <td>
                              <span className="badge bg-primary bg-opacity-10 text-primary">
                                {formatNumber(item.total_quantity)}
                              </span>
                            </td>
                            <td className="fw-semibold">{formatCurrency(item.total_cost)}</td>
                            <td>
                              <span className="badge bg-info bg-opacity-10 text-info">
                                {item.batch_count}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <EmptyState message="No stock summary available." />
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="row g-4 mt-1">
          <div className="col-lg-6">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <div>
                    <h5 className="card-title mb-0 d-flex align-items-center gap-2">
                      <i className="bi bi-archive text-danger" />
                      Dead Stock
                    </h5>
                    <small className="text-muted">
                      Period: {deadStock.selected_period?.toUpperCase() || 'N/A'}
                    </small>
                  </div>
                  <div className="d-flex flex-wrap gap-1 justify-content-end">
                    {['30d', '60d', '90d'].map(period => {
                      const isActive = period === deadStock?.selected_period
                      return (
                        <span
                          key={period}
                          role="button"
                          onClick={() => handleDeadStockPeriodClick(period)}
                          className={`badge rounded-pill ${
                            isActive ? 'bg-danger text-white' : 'bg-light text-dark'
                          }`}
                          style={{
                            cursor: isActive || deadStockLoading ? 'default' : 'pointer',
                            opacity: deadStockLoading && isActive ? 0.7 : 1
                          }}
                        >
                          {period.toUpperCase()}
                        </span>
                      )
                    })}
                  </div>
                </div>
                <p className="text-muted small mb-2">
                  Minimum sales required: <strong>{deadStock.minimum_sales_required ?? 0}</strong> units over the
                  selected period.
                </p>
                {deadStock.items?.length ? (
                  <div className="table-responsive mt-3">
                    <table className="table align-middle mb-0 table-hover">
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: '40%' }}>Product</th>
                          <th>Warehouse Stock</th>
                          <th>Last Sale</th>
                          <th>Sold in Period</th>
                        </tr>
                      </thead>
                      <tbody>
                        {deadStock.items.map(item => (
                          <tr key={item.product_id}>
                            <td>
                              <div className="fw-semibold d-flex align-items-center gap-2">
                                <span>{item.product_name}</span>
                              </div>
                              <small className="text-muted">{item.product_sku}</small>
                            </td>
                            <td>
                              <span className="badge bg-danger bg-opacity-10 text-danger">
                                {formatNumber(item.warehouse_stock)}
                              </span>
                            </td>
                            <td>
                              <small className="text-muted">
                                {item.last_sold_at ? formatDate(item.last_sold_at) : 'No sales yet'}
                              </small>
                            </td>
                            <td>
                              <span className="badge bg-secondary bg-opacity-10 text-secondary">
                                {formatNumber(item.sold_in_period)}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <EmptyState icon="bi bi-emoji-smile" message="Great news! No dead stock in this period." />
                )}
              </div>
            </div>
          </div>

          <div className="col-lg-6">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h5 className="card-title mb-0 d-flex align-items-center gap-2">
                      <i className="bi bi-hourglass-split text-warning"></i>
                      Slow Moving Stock
                    </h5>
                    <small className="text-muted">
                      Period: {slowStock.selected_period?.toUpperCase() || 'N/A'}
                    </small>
                  </div>
                  <div className="d-flex flex-column align-items-end gap-2">
                    <div className="d-flex flex-wrap gap-1 justify-content-end">
                      {['30d', '60d', '90d'].map(period => {
                        const isActive = period === slowStock?.selected_period
                        return (
                          <span
                            key={period}
                            role="button"
                            onClick={() => handleSlowStockPeriodClick(period)}
                            className={`badge rounded-pill ${
                              isActive ? 'bg-warning text-dark' : 'bg-light text-dark'
                            }`}
                            style={{
                              cursor: isActive || slowStockLoading ? 'default' : 'pointer',
                              opacity: slowStockLoading && isActive ? 0.7 : 1
                            }}
                          >
                            {period.toUpperCase()}
                          </span>
                        )
                      })}
                    </div>
                    <small className="text-muted">
                      Sales range: {slowStock.minimum_sales_threshold ?? 0} - {slowStock.maximum_sales_threshold ?? 0}
                    </small>
                  </div>
                </div>
                <p className="text-muted small mb-2">
                  Products with sales between <strong>{slowStock.minimum_sales_threshold ?? 0}</strong> and{' '}
                  <strong>{slowStock.maximum_sales_threshold ?? 0}</strong> units over the selected period.
                </p>
                {slowStock.items?.length ? (
                  <div className="table-responsive mt-3">
                    <table className="table align-middle mb-0 table-hover">
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: '40%' }}>Product</th>
                          <th>Sold In Period</th>
                          <th>Warehouse Stock</th>
                          <th>Last Sale</th>
                        </tr>
                      </thead>
                      <tbody>
                        {slowStock.items.map(item => (
                          <tr key={item.product_id}>
                            <td>
                              <div className="fw-semibold d-flex align-items-center gap-2">
                                <span>{item.product_name}</span>
                              </div>
                              <small className="text-muted">{item.product_sku}</small>
                            </td>
                            <td>
                              <span className="badge bg-warning bg-opacity-10 text-warning">
                                {formatNumber(item.sold_in_period)}
                              </span>
                            </td>
                            <td>{formatNumber(item.warehouse_stock)}</td>
                            <td>
                              <small className="text-muted">
                                {item.last_sold_at ? formatDate(item.last_sold_at) : 'No sales yet'}
                              </small>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <EmptyState icon="bi bi-check-circle" message="No slow moving stock detected for this period." />
                )}
              </div>
            </div>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <div className="pagetitle">
        <h1>Dashboard</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="/">Home</a></li>
            <li className="breadcrumb-item active">Dashboard</li>
          </ol>
        </nav>
      </div>

      <section className="section dashboard">
        {renderContent()}
      </section>
    </>
  )
}

export default Dashboard
